import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import DropdownEditMenu from '../../components/Dashboard/DropdownEditMenu'
import ResumeEventsChart from '../../components/Dashboard/ResumeEventsChart'
import DashboardConfigTaskModal from '../../components/Dashboard/DashboardConfigTaskModal'
import { useStateContext } from '../../components/contexts/ContextProvider'
import LoadContentSpinner from '../../components/Utils/LoadContentSpinner'
import axiosClient from '../../components/api/axiosClient'
import { formatDateToBar, formatNumber, formatPerformNumber } from '../../components/Utils/UtilsNumbersFormat'
import { formatPrice } from '../../components/Utils/UtilsPricesFormat'
import { useError } from '../../components/contexts/ErrorContext'
import InvoicesSections from '../../components/Dashboard/DashboardCommponent/InvoicesSections'
import DashboardPromoMarker from '../../components/Utils/DashboardPromoMarker'

const orderDisplayName = (order) =>
    order?.user_name ||
    order?.get_datas_to_order_customer?.username ||
    order?.get_datas_to_order_customer?.name ||
    '';

export default function HomeToDashboard() {

    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const { user, loading } = useStateContext();
    const { handleError } = useError();
    const [allTasksCompleted, setAllTasksCompleted] = useState(false);
    const [isAccountConfigured, setIsAccountConfigured] = useState(false);
    const [salesData, setSalesData] = useState(null); // État pour les événements
    const [loadingContent, setLoading] = useState(true); // État pour le chargement
    const [loadingDatasDash, setLoadingDatasDash] = useState(true); // État pour le chargement
    const [errorsDatasDash, setErrorsDatasDash] = useState(true); // État pour le chargement
    const [datasDash, setDatasDash] = useState([]); // État pour le chargement
    const [error, setError] = useState(null); // État pour gérer les erreurs
    const [openDescriptions, setOpenDescriptions] = useState([]);
    const [copiedLink, setCopiedLink] = useState({});
    const [activeEvents, setActiveEvents] = useState([]);
    const [loadingActiveEvents, setLoadingActiveEvents] = useState(true);

    const [loadingTasks, setLoadingTasks] = useState(true); // Ajout d'un état de chargement pour les tâches

    useEffect(() => {
        const fetchTasks = async () => {
            setLoadingTasks(true);
            try {
                const response = await axiosClient.get('/dashboard/tasks/getConfig');
                const stateAccount = response.data.account_state;
                setIsAccountConfigured(stateAccount);
                if (response.data.uncompletedTasks.length === 0) {
                    setAllTasksCompleted(true);
                } else {
                    setAllTasksCompleted(false);
                }
            } catch (error) {
                handleError("Erreur survenue lors de la récupération des configurations");
                // console.error("Erreur Axios capturée :", error);
            } finally {
                setLoadingTasks(false);
            }
        };

        fetchTasks();
    }, []); // L'effet s'exécute au démarrage

    const fetchSalesStatistics = async () => {
        // Appel API pour récupérer les événements en cours de validation
        setLoading(true);
        setError(null);
        
        axiosClient.get('/dashboard/global/organizer/sales-statistics')
            .then(response => {
                if (response.data.noContent) {
                    setError(response.data); // Afficher "Aucun résultat trouvé."
                } else {
                    // console.log(response.data.data)
                    setSalesData(response.data.data); // Mettez à jour la liste des événements
                }
                setLoading(false);
            })
            .catch(error => {
                if (error.response && error.response.data) {
                    // Récupérer les messages spécifiques de l'erreur backend
                    setError(error.response.data);
                } else {
                    setError({message: 'Impossible de récupérer les statistiques des ventes.',});
                }
                setLoading(false); // Arrêtez le chargement même en cas d'erreur
            });
    };

    // Utilisez useEffect pour appeler fetchSalesStatistics au montage du composant
    useEffect(() => {
        fetchSalesStatistics();
    }, []);

    useEffect(() => {
        const fetchDatasDashboard = async () => {
            setLoadingDatasDash(true);
            setErrorsDatasDash(null);
            // Appel API pour récupérer les événements en cours de validation
            axiosClient.get('/dashboard/getDatas/recap/dashboard')
                .then(response => {
                    setDatasDash(response.data.data); // Mettez à jour la liste des événements
                    setLoadingDatasDash(false);
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        // Récupérer les messages spécifiques de l'erreur backend
                        setErrorsDatasDash(error.response.data);
                    } else {
                        setErrorsDatasDash({message: 'Impossible de récupérer les statistiques des ventes.',});
                    }
                    setLoadingDatasDash(false); // Arrêtez le chargement même en cas d'erreur
                });
        };

        fetchDatasDashboard();
    }, []); // L'effet s'exécute au démarrage

    useEffect(() => {
        setLoadingActiveEvents(true);

        axiosClient
            .get('/dashboard/event-official/organizer/events', {
                params: { filter: 'published' },
            })
            .then((response) => {
                if (response.data.noContent) {
                    setActiveEvents([]);
                } else {
                    const withBilletterieLink = (response.data.data || []).filter(
                        (event) => event.lien_achat && String(event.lien_achat).trim() !== ''
                    );
                    setActiveEvents(withBilletterieLink);
                }
            })
            .catch(() => {
                setActiveEvents([]);
            })
            .finally(() => {
                setLoadingActiveEvents(false);
            });
    }, []);

    if (loading || loadingTasks || loadingDatasDash) {
        return <LoadContentSpinner />;
    }

    const handleAllTasksCompleted = () => {
        setAllTasksCompleted(true);
    };

    // Fonction pour ouvrir le modal
    const openFormModal = () => {
        setIsTaskModalOpen(true);
    };

    // Fonction pour fermer le modal
    const closeFormModal = () => {
        setIsTaskModalOpen(false);
    };

    // Fonction pour gérer l'ouverture/fermeture de la description
    const toggleDescription = (index) => {
        setOpenDescriptions(openDescriptions.includes(index) ? [] : [index]);
    };

    const handleCopy = (link) => {
        navigator.clipboard.writeText(link).then(
          () => {
            setCopiedLink((prevState) => ({ ...prevState, [link]: true })); // Marque le lien comme copié
            setTimeout(() => {
              setCopiedLink((prevState) => ({ ...prevState, [link]: false })); // Réinitialise après 3 secondes
            }, 3000);
          },
          (err) => {
            // console.error("Erreur lors de la copie : ", err);
            handleError("Échec de la copie du lien.");
          }
        );
    };
    
    const renderCopyIcon = (link) => {
        return copiedLink[link] ? (
          <svg className='text-green-500' viewBox="0 0 24 24" fill="currentColor" width={24} height={24}>
            <path d="M9.75 15.25 7 12.5 6 13.499 9.75 17.25 18 8.999 17 8z" />
          </svg>
        ) : (
            <svg
            viewBox="0 0 18 18"
            fill="currentColor"
            width={18}
            height={18}
            className="wds_1_146_2_ListItemAction__prefixIcon"
            data-hook="list-item-action-prefix-icon"
            >
            <path d="M14,4 C14.5522847,4 15,4.44771525 15,5 L15,12 C15,12.5522847 14.5522847,13 14,13 L13,13 L13,14 C13,14.5522847 12.5522847,15 12,15 L5,15 C4.44771525,15 4,14.5522847 4,14 L4,7 C4,6.44771525 4.44771525,6 5,6 L6,6 L6,5 C6,4.44771525 6.44771525,4 7,4 L14,4 Z M6,7 L5,7 L5,14 L12,14 L12,13 L7,13 C6.44771525,13 6,12.5522847 6,12 L6,7 Z M14,5 L7,5 L7,12 L14,12 L14,5 Z" />
            </svg>
        );
    };

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            {isAccountConfigured === 0 && (
                <div className="mb-6 gradient cvwie cosgb c33r0 cgnhv cb8zv cm84d csusu">
                    <div className="cqdkw cq5bq cqb5u">
                        <svg
                        width={44}
                        height={42}
                        xmlns="http://www.w3.org/2000/svg"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                        >
                        <defs>
                            <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="ill-b">
                            <stop stopColor="#5bcfc5" offset="0%" />
                            <stop stopColor="#5bcfc5" offset="100%" />
                            </linearGradient>
                            <linearGradient x1="50%" y1="24.537%" x2="50%" y2="100%" id="ill-c">
                            <stop stopColor="#4634B1" offset="0%" />
                            <stop stopColor="#4634B1" stopOpacity={0} offset="100%" />
                            </linearGradient>
                            <path id="ill-a" d="m20 0 20 40-20-6.25L0 40z" />
                        </defs>
                        <g
                            transform="scale(-1 1) rotate(-51 -11.267 67.017)"
                            fill="none"
                            fillRule="evenodd"
                        >
                            <mask id="ill-d" fill="#5bcfc5">
                            <use xlinkHref="#ill-a" />
                            </mask>
                            <use fill="url(#ill-b)" xlinkHref="#ill-a" />
                            <path
                            fill="url(#ill-c)"
                            mask="url(#ill-d)"
                            d="M20.586-7.913h25v47.5h-25z"
                            />
                        </g>
                        </svg>
                    </div>
                    {allTasksCompleted ? (
                        <div className="cm84d">

                            <div className="text-sm text-gray-700 ctv3j c1k3n c6f83">
                                <div className="c6f83">
                                Félicitations <b className="text-gray-900 dark:text-gray-100 c1k3n">{user.formatted_nom_complet}</b>{" "}
                                🎉, toutes vos configurations sont complètes !
                                </div>
                                
                                Votre profil est désormais prêt à publier tout type d'événement sur nos plateformes de vente en ligne 🤑.
                            </div>
                            <div className="text-right">
                                <Link to='/dashboard/events/registration' className="text-sm text-sky-500 hover:text-sky-500 inline-flex items-center ceetm c1k3n">
                                Publier un événement
                                <svg
                                    viewBox="0 0 18 18"
                                    fill="currentColor"
                                    width={18}
                                    height={18}
                                    className="wds_1_154_0_ButtonCore__suffix"
                                    data-hook="suffix-icon"
                                    >
                                    <path d="M14.2071068,9.5 L10.8503744,12.8518847 C10.6568213,13.0450456 10.3433767,13.0448866 10.1500196,12.8515296 C9.95663241,12.6581424 9.95638989,12.3446746 10.1494776,12.1509884 L12.2928932,10 L4,10 L4,9 L12.2928932,9 L10.1494776,6.84920158 C9.95638989,6.65551538 9.95663241,6.34204764 10.1500196,6.14866043 C10.3433767,5.95530338 10.6568213,5.95514443 10.8503744,6.14830528 L14.2071068,9.5 Z" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <div className="cm84d">
                            <div className="text-sm text-gray-700 ctv3j c1k3n c6f83">
                                <div className="c6f83">
                                Bienvenue <b className="text-gray-900 dark:text-gray-100 c1k3n">{user.formatted_nom_complet}</b>{" "}
                                👋, voici votre tableau de bord.
                                </div>

                                Il manque deux ou trois configurations à finaliser rapidement afin que votre profil organisateur soit totalement opérationnel sur nos plateformes.
                            </div>
                            <div className="text-right">
                                <button onClick={openFormModal} className="text-sm text-sky-500 hover:text-sky-500 inline-flex items-center ceetm c1k3n">
                                    Commencer 
                                    <svg
                                    viewBox="0 0 18 18"
                                    fill="currentColor"
                                    width={18}
                                    height={18}
                                    className="wds_1_154_0_ButtonCore__suffix"
                                    data-hook="suffix-icon"
                                    >
                                    <path d="M14.2071068,9.5 L10.8503744,12.8518847 C10.6568213,13.0450456 10.3433767,13.0448866 10.1500196,12.8515296 C9.95663241,12.6581424 9.95638989,12.3446746 10.1494776,12.1509884 L12.2928932,10 L4,10 L4,9 L12.2928932,9 L10.1494776,6.84920158 C9.95638989,6.65551538 9.95663241,6.34204764 10.1500196,6.14866043 C10.3433767,5.95530338 10.6568213,5.95514443 10.8503744,6.14830528 L14.2071068,9.5 Z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* Dashboard actions */}
            <div className="sm:flex sm:justify-between sm:items-center mb-8">

                {/* Left: Title */}
                <div className="mb-4 sm:mb-0">
                    <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">Tableau de bord</h1>
                </div>
                <div className="cm3b7 c51uw ccww4 csdex cbe1i c4sak">
                        {/* Create Events button */}
                        <Link to={'/dashboard/events/registration'} className="btn bg-primary-600 hover:bg-primary-700 text-white cdj8c cg0jr ch8z9 cilvw cyn7a">
                            <svg
                                className="fill-current shrink-0 visible block mr-2 cbmv0 coqgc"
                                width={15}
                                height={15}
                                viewBox="0 0 15 15"
                            >
                                <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                            </svg>
                            <span className="">Créer un événement</span>
                        </Link>
                    </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-12 gap-6">
                
                <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-gray-800 shadow-sm rounded-xl">
                    <div className="px-5 py-5">
                        <header className="flex justify-between items-start mb-2">
                        <h2 className="text-base text-gray-800 dark:text-gray-100 mb-2">Total des tickets</h2>
                        {/* Menu button */}
                        <DropdownEditMenu align="left" className="relative inline-flex max-w-full">
                            <li>
                            <Link to={'/dashboard/events/registration'} className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3">
                                Nouvel événement
                            </Link>
                            </li>
                            <li>
                            <Link to={'/dashboard/events/show-list'} className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3">
                                Mes événements
                            </Link>
                            </li>
                        </DropdownEditMenu>
                        </header>
                        <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Vendus</div>
                        {errorsDatasDash ? (
                            <span className='text-gray-800 dark:text-gray-100 font-bold text-xs'>...</span>
                        ) : (
                            <div className="flex items-start">
                                <div className="text-xl font-bold text-gray-800 dark:text-gray-100 mr-2">{formatPrice(datasDash.total_gross_revenue)}</div>
                                {/* <div className="text-sm font-medium text-green-700 px-1.5 bg-green-500/20 rounded-full">+49%</div> */}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-gray-800 shadow-sm rounded-xl">
                    <div className="px-5 py-5">
                        <header className="flex justify-between items-start mb-2">
                        <h2 className="text-base text-gray-800 dark:text-gray-100 mb-2">Revenus générés</h2>
                        {/* Menu button */}
                        <DropdownEditMenu align="left" className="relative inline-flex">
                            <li>
                            <Link to={'/dashboard/ventes/statistiques'} className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3">
                                Voir les chiffres
                            </Link>
                            </li>
                            <li>
                            <Link to={'/dashboard/factures/events/invoices'} className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3">
                                Mes versemants
                            </Link>
                            </li>
                        </DropdownEditMenu>
                        </header>
                        <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Ce mois</div>
                        {errorsDatasDash ? (
                            <span className='text-gray-800 dark:text-gray-100 font-bold text-xs'>...</span>
                        ) : (
                            <div className="flex items-start">
                                <div className="text-xl font-bold text-gray-800 dark:text-gray-100 mr-2">{formatPrice(datasDash.total_gross_revenue_this_month)}</div>
                                {/* <div className="text-sm font-medium text-red-700 px-1.5 bg-red-500/20 rounded-full">-14%</div> */}
                            </div>
                        )}
                    </div>
              
                </div>

                <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-gray-800 shadow-sm rounded-xl">
                    <div className="px-5 py-5">
                        <header className="flex justify-between items-start mb-2">
                        <h2 className="text-base text-gray-800 dark:text-gray-100 mb-2">Tickets restants</h2>
                        </header>
                        <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Des événements actifs</div>
                        {errorsDatasDash ? (
                            <span className='text-gray-800 dark:text-gray-100 font-bold text-xs'>...</span>
                        ) : (

                            <div className="flex items-start">
                                <div className="text-xl font-bold text-gray-800 dark:text-gray-100 mr-2">{formatPerformNumber(datasDash.total_tickets_remaining)}</div>
                                {/* <div className="text-sm font-medium text-green-700 px-1.5 bg-green-500/20 rounded-full">+49%</div> */}
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-span-full xl:col-span-7 bg-white dark:bg-gray-800 shadow-sm rounded-xl h-fit">
                    <header className="flex items-center cghq3 cbv37 ctv3r cx3hp cz8qb">
                        <h2 className="text-gray-800 dark:text-gray-100 cgulq">Résumé des Événements</h2>
                    </header>
                    <div className="cx3hp cwn3v ">
                        {errorsDatasDash ? (
                            <div className="w-full h-full flex justify-center items-center cweej">
                                <div className="cydwr clbq0">
                                    <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                                        <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                                            <path
                                                className="text-gray-500 co1wq"
                                                d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                                fill="currentColor"
                                            />
                                            <path
                                                className="dark:text-gray-400 ckbo4"
                                                d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                                fill="currentColor"
                                            />
                                            <path
                                                className="cmpw7 cdqku"
                                                d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                    </div>
                                    <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                                    Rien à afficher
                                    </h4>
                                    <div className="mb-6 text-sm break-all">
                                    {errorsDatasDash.message}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-wrap ccue7">
                                    {/* Unique Visitors */}
                                    <div className="flex items-center cuvgf">
                                        <div className="clxab">
                                            <div className="flex items-center">
                                                <div className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mr-2">
                                                {formatNumber(datasDash.all_events)}
                                                </div>
                                                {/* <div className="text-sm cdjj4 c1k3n">+49%</div> */}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                Total événement
                                            </div>
                                        </div>
                                        <div
                                        className="hidden dark:bg-gray-700 cvwbh cyqqd clxab cn8zk cue4z"
                                        aria-hidden="true"
                                        />
                                    </div>
                                    {/* Total Pageviews */}
                                    <div className="flex items-center cuvgf">
                                        <div className="clxab">
                                        <div className="flex items-center">
                                            <div className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mr-2">
                                            {formatNumber(datasDash.unpublished_events)}
                                            </div>
                                            {/* <div className="text-sm cdjj4 c1k3n">+7%</div> */}
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            Attente de validation
                                        </div>
                                        </div>
                                        <div
                                        className="hidden dark:bg-gray-700 cvwbh cyqqd clxab cn8zk cue4z"
                                        aria-hidden="true"
                                        />
                                    </div>
                                    {/* Bounce Rate */}
                                    <div className="flex items-center cuvgf">
                                        <div className="clxab">
                                            <div className="flex items-center">
                                                <div className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mr-2">
                                                {formatNumber(datasDash.published)}
                                                </div>
                                                <div className="flex items-center justify-center w-3 h-3 cm84d chfzq">
                                                    <div className='inline-flex rounded-full cu2fn cattr cr9x4 cqdkw cav8x c6btv' />
                                                    <div className='inline-flex rounded-full cg902 cm84d csh1p co6q2' />
                                                    
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                Publié
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {datasDash.all_events > 0 && (

                                    <ResumeEventsChart all={datasDash.all_events} published={datasDash.published} unpublished={datasDash.unpublished_events} error={errorsDatasDash} />
                                )}
                            </>
                        )}
                    </div>
                    
                </div>
                
                <div className='flex flex-col col-span-full sm:col-span-6 xl:col-span-5 bg-white dark:bg-gray-800 shadow-sm rounded-xl'>
                    <header className="flex items-center justify-between cghq3 cbv37 ctv3r cx3hp cz8qb">
                        <div>
                            <h2 className="text-gray-800 dark:text-gray-100 cgulq">Mes liens de billetterie</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Événements actifs</p>
                        </div>
                        <Link
                            to="/dashboard/events/show-list"
                            className="text-xs text-blue-500 hover:text-blue-600 font-medium shrink-0"
                        >
                            Voir tout
                        </Link>
                    </header>
                    <div className="cx3hp cwn3v">
                        {loadingActiveEvents ? (
                            <div className="py-8">
                                <LoadContentSpinner />
                            </div>
                        ) : activeEvents.length === 0 ? (
                            <div className="px-5 py-6 text-center">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Aucun événement actif avec un lien billetterie enregistré.
                                </p>
                                <Link
                                    to="/dashboard/events/registration"
                                    className="inline-block mt-3 text-sm text-blue-500 hover:text-blue-600 font-medium"
                                >
                                    Publier un événement
                                </Link>
                            </div>
                        ) : (
                            <div className="px-5 py-3 space-y-5 max-h-[28rem] overflow-y-auto">
                                {activeEvents.map((event) => {
                                    const purchaseUrl = String(event.lien_achat).trim();

                                    return (
                                        <div
                                            key={event.id}
                                            className="border-b border-gray-100 dark:border-gray-700/60 pb-5 last:border-0 last:pb-0"
                                        >
                                            <div className="flex items-start gap-3 mb-3">
                                                {event.affiche ? (
                                                    <img
                                                        src={`data:image/jpeg;base64,${event.affiche}`}
                                                        alt={event.name}
                                                        className="w-14 h-14 rounded-lg object-cover shrink-0"
                                                    />
                                                ) : (
                                                    <div className="w-14 h-14 rounded-lg bg-gray-100 dark:bg-gray-700 shrink-0" />
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <Link
                                                        to={`/dashboard/events/view-details/to/${event.id}`}
                                                        className="text-sm font-semibold text-gray-800 dark:text-gray-100 hover:text-primary-600 line-clamp-2"
                                                    >
                                                        {event.name}
                                                    </Link>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        {event.date}
                                                        {event.hour ? ` · ${event.hour}` : ''}
                                                    </p>
                                                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                                        <span className="inline-flex text-xs rounded-full c1lu4 c19il cydwr c1k3n cdw1w c9hxi">
                                                            {event.status || 'Disponible'}
                                                        </span>
                                                        <DashboardPromoMarker
                                                            eventId={event.id}
                                                            hasActivePromo={Boolean(event.has_active_promo)}
                                                            activeCount={event.active_promotions_count}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <label className="block text-xs c1k3n cu6vl">
                                                Lien billetterie
                                            </label>
                                            <div className="cm84d">
                                                <input
                                                    className="!text-xs !pr-24 caqf9 c6btv cdo3e select-none"
                                                    readOnly
                                                    value={purchaseUrl}
                                                />
                                                <div className="flex items-center cimu8 cqdkw cini7">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCopy(purchaseUrl)}
                                                        className="text-sm cmpw7 cdqku c1k3n pr-2 font-semibold text-gray-800 dark:text-gray-200"
                                                        title="Copier le lien"
                                                    >
                                                        {copiedLink[purchaseUrl] ? 'Copié' : renderCopyIcon(purchaseUrl)}
                                                    </button>
                                                    <span className="text-xs border-gray-300 dark:border-gray-600 border-l font-semibold text-gray-800 dark:text-gray-200 cmpw7 cdqku c1k3n pr-3 pl-2">
                                                        <a
                                                            href={purchaseUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Visiter
                                                        </a>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                
                {datasDash.invoices?.length > 0 && (
                    <InvoicesSections invoices={datasDash.invoices} />
                )}

                <div className="col-span-full xl:col-span-12 bg-white dark:bg-gray-800 shadow-sm rounded-xl">
                    <header className="px-5 py-4 inline-flex items-center justify-between sm:flex-row flex-col w-full border-b border-gray-100 dark:border-gray-700/60">
                        <h2 className="font-semibold text-gray-800 dark:text-gray-100">10 Événements récents </h2>
                        <Link to={`/dashboard/ventes/tickets-vendus/showAll`} className="text-blue-500 inline-flex items-center text-xs c5ylh c1k3n">
                            Voir plus
                            {/* <span className="c5fh5">  */}
                                <svg
                                viewBox="0 0 18 18"
                                fill="currentColor"
                                width={18}
                                height={18}
                                className="wds_1_154_0_ButtonCore__suffix"
                                data-hook="suffix-icon"
                                >
                                <path d="M14.2071068,9.5 L10.8503744,12.8518847 C10.6568213,13.0450456 10.3433767,13.0448866 10.1500196,12.8515296 C9.95663241,12.6581424 9.95638989,12.3446746 10.1494776,12.1509884 L12.2928932,10 L4,10 L4,9 L12.2928932,9 L10.1494776,6.84920158 C9.95638989,6.65551538 9.95663241,6.34204764 10.1500196,6.14866043 C10.3433767,5.95530338 10.6568213,5.95514443 10.8503744,6.14830528 L14.2071068,9.5 Z" />
                                </svg>
                            {/* </span> */}
                        </Link>
                    </header>
                    <div className="p-3">
                        {/* Table */}
                        {loadingContent ? (
                            <LoadContentSpinner />
                        ) : (
                            error ? (
                                <div className="w-full h-full flex justify-center items-center cweej">
                                    <div className="cydwr clbq0">
                                        <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                                            <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                                                <path
                                                    className="text-gray-500 co1wq"
                                                    d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    className="dark:text-gray-400 ckbo4"
                                                    d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    className="cmpw7 cdqku"
                                                    d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                        </div>
                                        <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                                        Rien à afficher
                                        </h4>
                                        <div className="mb-6 text-sm">
                                        {error.message}
                                        </div>
                                    </div>
                                </div>
                            ) : (

                                salesData.eventsList?.length > 0 ? (
    
                                    <div className="overflow-x-auto">
                                        <table className="table-auto w-full dark:text-gray-300">
                                            {/* Table header */}
                                            <thead className="text-xs uppercase text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 dark:bg-opacity-50 rounded-sm">
                                                <tr>
                                                    <th className="whitespace-nowrap p-2">
                                                    <div className="font-semibold text-left w-fit">A Propos</div>
                                                    </th>
                                                    <th className="whitespace-nowrap p-2">
                                                    <div className="font-semibold text-center">Total en vente</div>
                                                    </th>
                                                    <th className="whitespace-nowrap p-2">
                                                    <div className="font-semibold text-center">Vendus</div>
                                                    </th>
                                                    <th className="whitespace-nowrap p-2">
                                                    <div className="font-semibold text-center">Restants</div>
                                                    </th>
                                                    <th className="whitespace-nowrap p-2">
                                                    <div className="font-semibold text-left">statut</div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            {/* Table body */}
                                            <tbody className="text-sm font-medium divide-y divide-gray-100 dark:divide-gray-700/60">
                                            {/* Row */}
                                            {salesData.eventsList.map((event, index) => (
                                                <tr key={index}>
                                                    <td className="inline-block p-2">
                                                        <div className="flex items-center">
                                                            <img 
                                                                src={`data:image/jpeg;base64,${event.event_banner}`}
                                                                alt={event.name} className="w-16 rounded-lg shrink-0 mr-2 sm:mr-3"
                                                            />
                                                            <div className="text-gray-800 dark:text-gray-100 truncate w-72">{event.event_name}</div>
                                                        </div>
                                                    </td>
                                                    <td className="p-2">
                                                    <div className="text-center">{formatNumber(event.total_tickets)}</div>
                                                    </td>
                                                    <td className="p-2">
                                                    <div className="text-center text-green-500">{formatNumber(event.tickets_sold)}</div>
                                                    </td>
                                                    <td className="p-2">
                                                    <div className="text-center">{formatNumber(event.tickets_remaining)}</div>
                                                    </td>
                                                    <td className="p-2">
                                                        {event.annule ? (
                                                            <div className="inline-flex text-xs rounded-full cx6qj cz4gk cydwr c1k3n cdw1w c9hxi">
                                                                Annulé
                                                            </div>
                                                        ) : (
    
                                                            event.status === 'Disponible' ? (
                                                                <div className="inline-flex text-xs rounded-full c1lu4 c19il cydwr c1k3n cdw1w c9hxi">
                                                                    {event.status}
                                                                </div>
                                                            ) : event.status === 'Disabled' ? (
                                                                <div className="inline-flex text-xs rounded-full cx6qj cz4gk cydwr c1k3n cdw1w c9hxi">
                                                                    Désactivé
                                                                </div>
                                                            ) : (
                                                                <div className="inline-flex text-xs rounded-full clksd cydwr c1k3n cdw1w c9hxi">
                                                                    {event.status}
                                                                </div>
                                                            )
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="w-full h-full flex justify-center items-center cweej">
                                        <div className="cydwr clbq0">
                                            <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                                                <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                                                    <path
                                                        className="text-gray-500 co1wq"
                                                        d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                                        fill="currentColor"
                                                    />
                                                    <path
                                                        className="dark:text-gray-400 ckbo4"
                                                        d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                                        fill="currentColor"
                                                    />
                                                    <path
                                                        className="cmpw7 cdqku"
                                                        d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            </div>
                                            <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                                            Aucune Données
                                            </h4>
                                        </div>
                                    </div>
                                )
                            )
                        )}
                    </div>
                </div>

                <div className="col-span-full xl:col-span-12 bg-white dark:bg-gray-800 shadow-sm rounded-xl">
                    <header className="px-5 py-4 inline-flex items-center justify-between sm:flex-row flex-col w-full border-b border-gray-100 dark:border-gray-700/60">
                        <h2 className="font-semibold text-gray-800 dark:text-gray-100">10 Paiements récents </h2>
                        <Link to={`/dashboard/ventes/tickets/paiements/mes-paiements`} className="text-blue-500 inline-flex items-center text-xs c5ylh c1k3n">
                            Voir plus
                            {/* <span className="c5fh5">  */}
                                <svg
                                viewBox="0 0 18 18"
                                fill="currentColor"
                                width={18}
                                height={18}
                                className="wds_1_154_0_ButtonCore__suffix"
                                data-hook="suffix-icon"
                                >
                                <path d="M14.2071068,9.5 L10.8503744,12.8518847 C10.6568213,13.0450456 10.3433767,13.0448866 10.1500196,12.8515296 C9.95663241,12.6581424 9.95638989,12.3446746 10.1494776,12.1509884 L12.2928932,10 L4,10 L4,9 L12.2928932,9 L10.1494776,6.84920158 C9.95638989,6.65551538 9.95663241,6.34204764 10.1500196,6.14866043 C10.3433767,5.95530338 10.6568213,5.95514443 10.8503744,6.14830528 L14.2071068,9.5 Z" />
                                </svg>
                            {/* </span> */}
                        </Link>
                    </header>
                    <div className="p-3">
                        {errorsDatasDash ? (
                            <div className="w-full h-full flex justify-center items-center cweej">
                                <div className="cydwr clbq0">
                                    <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                                        <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                                            <path
                                                className="text-gray-500 co1wq"
                                                d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                                fill="currentColor"
                                            />
                                            <path
                                                className="dark:text-gray-400 ckbo4"
                                                d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                                fill="currentColor"
                                            />
                                            <path
                                                className="cmpw7 cdqku"
                                                d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                    </div>
                                    <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                                    Rien à afficher
                                    </h4>
                                    <div className="mb-6 text-sm break-all">
                                    {errorsDatasDash.message}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            datasDash.orders.length > 0 ? (

                                <div className="overflow-x-auto">
                                    <table className="table-auto w-full dark:text-gray-300">
                                        {/* Table header */}
                                        <thead className="text-xs uppercase text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 dark:bg-opacity-50 rounded-sm">
                                            <tr>
                                                <th className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                    <div className="cgulq c2hoo">ID achat</div>
                                                </th>
                                                <th className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                    <div className="cgulq c2hoo">Date d'achat</div>
                                                </th>
                                                <th className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                    <div className="cgulq c2hoo">Acheteur</div>
                                                </th>
                                                <th className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                    <div className="cgulq c2hoo">Montant</div>
                                                </th>
                                                <th className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                    <div className="cgulq c2hoo">Statut</div>
                                                </th>
                                                <th className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                    <div className="cgulq">Quantité</div>
                                                </th>
                                                <th className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                    <span className="cn8jz">Menu</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-sm font-medium divide-y divide-gray-100 dark:divide-gray-700/60">
                                            {/* Row */}
                                            {datasDash.orders.map((order, index) => (

                                                <React.Fragment key={index}>

                                                <tr>
                                                    <td className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                        {!order.transaction_id ? (
                                                            <>...</>
                                                        ) : (
                                                            <div className="flex items-center text-gray-800">
                                                                <div className="c740r c1k3n">#{order.transaction_id.slice(-6)}</div>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                        {!order.date_payment ? (
                                                            <>...</>
                                                        ) : (
                                                            <div>{formatDateToBar(order.date_payment)} </div>
                                                        )}
                                                    </td>
                                                    <td className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                        {!orderDisplayName(order) ? (
                                                            <>...</>
                                                        ) : order.get_datas_to_order_customer?.id ? (
                                                            <Link to={`/dashboard/achats/tickets/clients/profil/p/${order.get_datas_to_order_customer.id}`}>
                                                                <div className="text-gray-800 dark:text-gray-100 c1k3n">
                                                                    {orderDisplayName(order)}
                                                                </div>
                                                            </Link>
                                                        ) : (
                                                            <div className="text-gray-800 dark:text-gray-100 c1k3n">
                                                                {orderDisplayName(order)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                        {!order.total_price ? (
                                                            <>...</>
                                                        ) : (
                                                            <div className="cdjj4 c1k3n c2hoo">{formatPrice(order.total_price)}</div>
                                                        )}
                                                    </td>
                                                    <td className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                        {!order.status ? (
                                                            <>...</>
                                                        ) : (
                                                            order.status === 'ACCEPTED' ? (

                                                                <div className="inline-flex text-xs rounded-full c1lu4 c19il cydwr c1k3n ch4gv cdw1w">
                                                                    {order.status}
                                                                </div>
                                                            ) : order.status === 'REFUSED' ? (
                                                                
                                                                <div className='inline-flex text-xs rounded-full cx6qj cz4gk cydwr c1k3n ch4gv cdw1w'>
                                                                    {order.status}
                                                                </div>
                                                                
                                                            ) : (
                                                                <div className="inline-flex text-xs rounded-full cfts0 c5px7 cydwr c1k3n ch4gv cdw1w">
                                                                    {order.status}
                                                                </div>
                                                            )
                                                        )}
                                                    </td>
                                                    <td className="cq84g cyjcc cgn91 c9hxi c72q5">
                                                        {!order.quantity ? (
                                                            <>...</>
                                                        ) : (
                                                            <div className="cydwr">{formatNumber(order.quantity)}</div>
                                                        )}
                                                    </td>
                                                    <td className="cq84g cyjcc cgn91 cn8zk c9hxi c72q5">
                                                        <div className="flex items-center">
                                                            <button
                                                            className="bg-white border-2 border-gray-50 rounded-full text-gray-900 font-bold cxxol c5vqk c3e4j cg12x cmpw7 cdqku c98dn"
                                                            aria-expanded={openDescriptions.includes(index) ? "true" : "false"}
                                                            onClick={() => toggleDescription(index)}
                                                            >
                                                            <span className="cn8jz">Menu</span>
                                                            <svg className="cbm9w cue4z cmwfi" viewBox="0 0 32 32">
                                                                <path d="M16 20l-5.4-5.4 1.4-1.4 4 4 4-4 1.4 1.4z" />
                                                            </svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {openDescriptions.includes(index) && (
                                                    <tr id="description-01">
                                                        <td colSpan={10} className="cyjcc cgn91 c9hxi c72q5">
                                                        <div className="bg-sidebar-linkGroup rounded-md animate flex items-center flex-wrap -m-1 dark:text-gray-400 coulr c1f2y c9j8s">
                                                            <div className="text-sm mr-4">
                                                                <h3 className="text-gray-800 font-semibold dark:text-gray-100 c1k3n">
                                                                {!order.pivot_to_order.findthislabel.title ? (
                                                                    <>...</>
                                                                ) : (
                                                                    order.pivot_to_order.findthislabel.title
                                                                )}
                                                                </h3>
                                                                <div>Type de Ticket</div>
                                                            </div>
                                                            <div class="co1wq cdqku mr-4">·</div>
                                                            <div className="text-sm mr-4">
                                                                <h3 className="text-gray-800 font-semibold dark:text-gray-100 c1k3n">
                                                                {!order.payment_method ? (
                                                                    <>...</>
                                                                ) : (
                                                                    order.payment_method
                                                                )}
                                                                </h3>
                                                                <div>Payé par</div>
                                                            </div>
                                                            <div class="co1wq cdqku mr-4">·</div>
                                                            <div className="text-sm mr-4">
                                                                <h3 className="text-gray-800 font-semibold dark:text-gray-100 c1k3n">
                                                                {!order.get_datas_to_order_event.event_code ? (
                                                                    <>...</>
                                                                ) : (
                                                                    <Link to={`/dashboard/events/view-details/to/${order.get_datas_to_order_event.id}`}>

                                                                        {order.get_datas_to_order_event.event_code}
                                                                    </Link>
                                                                )}
                                                                </h3>
                                                                <div>Code événement</div>
                                                            </div>
                                                            <div class="co1wq cdqku mr-4">·</div>
                                                            <div className="text-sm mr-4">
                                                                <h3 className="text-gray-800 font-semibold dark:text-gray-100 c1k3n">
                                                                {!order.get_datas_to_order_customer.adresse ? (
                                                                    <>...</>
                                                                ) : (
                                                                    order.get_datas_to_order_customer.adresse
                                                                )}
                                                                </h3>
                                                                <div>Adresse</div>
                                                            </div>
                                                        </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}

                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="w-full h-full flex justify-center items-center cweej">
                                    <div className="cydwr clbq0">
                                        <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                                            <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                                                <path
                                                    className="text-gray-500 co1wq"
                                                    d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    className="dark:text-gray-400 ckbo4"
                                                    d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    className="cmpw7 cdqku"
                                                    d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                        </div>
                                        <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                                        Aucune Données
                                        </h4>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
            {/* Modal to add card */}
            <DashboardConfigTaskModal 
                onAllTasksCompleted={handleAllTasksCompleted}
                isTaskModalOpen={isTaskModalOpen}
                closeModal={closeFormModal}
            />

        </div>
    )
}
